import isEqual from 'lodash/isEqual'
import { ComponentSurfaceWrappingMode } from 'types/assetModel/componentSurfaceWrappingMode'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'

import { GeckoComponentCoords } from './geckoComponentCoords'
import {
  determineXDirection,
  determineYDirection,
  GeckoComponentPath,
  LineDirectionX,
  LineDirectionY,
} from './geckoComponentPath'
import { GeckoComponentRect } from './geckoComponentRect'
import { GeckoComponentSpace } from './geckoComponentSpace'

/**
 * The orientation of a polygon:
 *  Positive orientation (points drawn counterclockwise) means that the bounding
 *  box is on the outside of the polygon.
 *  Negative orientation (points drawn clockwise) means that the bounding box is
 *  on the inside of the polygon.
 */
export enum PolygonOrientation {
  PositivelyOriented = 'CounterClockwise',
  NegativelyOriented = 'Clockwise',
}

/**
 * A polygon is a closed path with the same start and end point.
 */
export class GeckoComponentPolygon extends GeckoComponentPath {
  constructor(
    points: ReadonlyArray<GeckoComponentCoords>,
    pathDirectionX: ReadonlyArray<LineDirectionX> | undefined = undefined,
    pathDirectionY: ReadonlyArray<LineDirectionY> | undefined = undefined
  ) {
    super(points, pathDirectionX, pathDirectionY)
    if (!GeckoComponentCoords.Equals(points.at(0), points.at(-1))) {
      console.error(
        'Polygon with the following coordinates is not closed, and will lead to undefined behavior:',
        points
      )
    }
  }

  public static CreateFromPath(
    path: GeckoComponentPath
  ): GeckoComponentPolygon {
    if (GeckoComponentCoords.Equals(path.points.at(0), path.points.at(-1))) {
      return new GeckoComponentPolygon(
        path.points,
        path.pathDirectionX,
        path.pathDirectionY
      )
    }
    return GeckoComponentPolygon.CreateFromPath(
      GeckoComponentPath.CreateFromCoords([...path.points, path.points[0]])
    )
  }

  /**
   * Creates a GeckoComponentPolygon for a polygon. If component wraps, takes the shortest path between
   * the two points. Assumes component will not wrap in both y and x direction -- undetermined
   * behavior in this case.
   *
   * The coordinates in this polygon will be tagged to the component-id from the componentBounds prop
   *
   * @param polygon - list of points. Assumes first point === last point, and that coordinates are
   * in the same space as the componentBounds.
   * @param componentBounds
   * @param wrapsX
   * @param wrapsY
   * @returns
   */
  public static CreateFromPolygonalPlate(
    polygon: { x: GeckoMeasurement; y: GeckoMeasurement }[],
    componentBounds: GeckoComponentRect,
    wrapMode: ComponentSurfaceWrappingMode
  ): GeckoComponentPath {
    const xDirections = []
    const firstPoint = polygon[0]
    const polygonUnits = GeckoComponentSpace.CreateFromMeasurementUnits(
      componentBounds.componentID,
      firstPoint.x.unit,
      firstPoint.y.unit
    )
    const projectedComponentBounds = componentBounds.project(polygonUnits)

    const wrapsX =
      wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsX ||
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsX
    const wrapsY =
      wrapMode === ComponentSurfaceWrappingMode.ComponentWrapsY ||
      wrapMode === ComponentSurfaceWrappingMode.UnrolledComponentWrapsY

    if (wrapsX) {
      for (let i = 0; i < polygon.length; i++) {
        const sx = polygon[i % polygon.length].x.toNumber(
          componentBounds.x.unit
        )
        const ex = polygon[(i + 1) % polygon.length].x.toNumber(
          componentBounds.x.unit
        )
        const lineWraps = determineIfLineWraps(
          sx,
          ex,
          projectedComponentBounds.x.delta.num
        )

        xDirections.push(determineXDirection(sx, ex, lineWraps))
      }
    } else {
      for (let i = 0; i < polygon.length; i++) {
        const sx = polygon[i % polygon.length].x.toNumber(
          componentBounds.x.unit
        )
        const ex = polygon[(i + 1) % polygon.length].x.toNumber(
          componentBounds.x.unit
        )
        xDirections.push(determineXDirection(sx, ex))
      }
    }
    const yDirections = []
    if (wrapsY) {
      for (let i = 0; i < polygon.length; i++) {
        const sy = polygon[i % polygon.length].y.toNumber(
          componentBounds.y.unit
        )
        const ey = polygon[(i + 1) % polygon.length].y.toNumber(
          componentBounds.y.unit
        )
        const lineWraps = determineIfLineWraps(
          sy,
          ey,
          projectedComponentBounds.y.delta.num
        )
        yDirections.push(determineYDirection(sy, ey, lineWraps))
      }
    } else {
      for (let i = 0; i < polygon.length; i++) {
        const sy = polygon[i % polygon.length].y.toNumber(
          componentBounds.y.unit
        )
        const ey = polygon[(i + 1) % polygon.length].y.toNumber(
          componentBounds.y.unit
        )
        yDirections.push(determineYDirection(sy, ey))
      }
    }
    return GeckoComponentPolygon.CreateFromPath(
      GeckoComponentPath.CreateFromValues(
        componentBounds.componentID,
        isEqual(polygon[0], polygon.at(-1))
          ? polygon.map((point) => ({
              x: point.x.toNumber(polygonUnits.x.unit),
              y: point.y.toNumber(polygonUnits.y.unit),
            }))
          : [
              ...polygon.map((point) => ({
                x: point.x.toNumber(polygonUnits.x.unit),
                y: point.y.toNumber(polygonUnits.y.unit),
              })),
              {
                x: polygon[0].x.toNumber(polygonUnits.x.unit),
                y: polygon[0].y.toNumber(polygonUnits.y.unit),
              },
            ],
        polygonUnits.x.unit,
        polygonUnits.y.unit,
        xDirections,
        yDirections
      )
    )
  }

  /**
   * A factory method for creating a GeckoComponentPolygon from a rect. Rects
   * that wrap should add infinite points between points that wrap.
   * @param rect          The rect to create a component path from
   * @constructor
   */
  public static CreateFromRect(
    rect: GeckoComponentRect
  ): GeckoComponentPolygon {
    const x_start = rect.x.start.num
    const y_start = rect.y.start.num
    const x_end = rect.x.end.num
    const y_end = rect.y.end.num
    return GeckoComponentPolygon.CreateFromPath(
      GeckoComponentPath.CreateFromValues(
        rect.componentID,
        [
          { x: x_start, y: y_start },
          { x: x_end, y: y_start },
          { x: x_end, y: y_end },
          { x: x_start, y: y_end },
          { x: x_start, y: y_start }, // Polygons should loop back to start
        ],
        rect.x.unit,
        rect.y.unit,
        [
          LineDirectionX.Right,
          LineDirectionX.None,
          LineDirectionX.Left,
          LineDirectionX.None,
        ],
        [
          LineDirectionY.None,
          LineDirectionY.Up,
          LineDirectionY.None,
          LineDirectionY.Down,
        ]
      )
    )
  }

  /**
   * Use the shoelace formula (or surveyor's formula) to determine the signed area of a polygon.
   * The polygon is oriented clockwise if the signed area is positive, and counterclockwise if
   * the signed area is negative.
   */
  public static getPolygonOrientation(
    polygon: GeckoComponentPolygon
  ): PolygonOrientation {
    const shoelaceSum = polygon.points.reduce((acc, curr, idx) => {
      const next = polygon.points[(idx + 1) % polygon.points.length]
      return acc + (curr.x.num * next.y.num - curr.y.num * next.x.num)
    }, 0)
    return shoelaceSum > 0
      ? PolygonOrientation.PositivelyOriented
      : PolygonOrientation.NegativelyOriented
  }
}

/**
 * Returns if the difference between start and end is greater than half of the range width
 */
function determineIfLineWraps(s: number, e: number, rangeWidth: number) {
  return Math.abs(e - s) > rangeWidth / 2
}
